/**
 * Client Configuration
 *
 * This is the centralized configuration file for client-specific customization.
 * Modify this file to rebrand the application for different clients.
 *
 * IMPORTANT: This file contains all customizable elements including:
 * - Color palettes for all theme modes
 * - Company branding information
 * - Logo paths
 * - Map coordinates
 * - Application metadata
 */

// Import logo assets
import CompanyLogo from '../assets/company-logo.svg';
import CompanyLogoLight from '../assets/company-logo-light.svg';
import MapImage from '../assets/mapa.png';
import { ClientConfig } from './client-config.types';

/**
 * Default Client Configuration - Arcelor Mittal
 *
 * To customize for a new client:
 * 1. Update branding information (company name, logos, contact info)
 * 2. Modify color palettes to match client branding
 * 3. Update map coordinates to client location
 * 4. Replace logo assets in src/assets/ folder
 * 5. Update app metadata
 */
export const clientConfig: ClientConfig = {
  // Application metadata
  app: {
    name: 'Salud 4.0',
    version: '1.1.0',
    description: 'Worker Tracking System',
    shortName: 'Salud 4.0',
    themeColor: '#ff3700',
    backgroundColor: '#ffffff'
  },

  // Company/Client branding
  branding: {
    companyName: 'ArcelorMittal',
    companyFullName: 'ArcelorMittal Costa Rica',
    logoAltText: 'ArcelorMittal Logo',
    contactInfo: {
      email: 'info@arcelormittal.com',
      phone: '+506 2000-0000',
      address: 'Costa Rica'
    }
  },

  // Logo configuration for different themes
  logos: {
    light: CompanyLogo,
    dark: CompanyLogoLight,
    highContrast: CompanyLogoLight,
    christmas: CompanyLogo
  },

  // Map configuration
  map: {
    defaultLocation: {
      latitude: 10.25138867447405,
      longitude: -83.74598415762124,
      zoom: 17,
      pitch: 40
    },
    mapImage: MapImage
  },
  general: {
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      }
    },
    shape: {
      borderRadius: 12 // Default is 4px
    },
    spacing: 10,
    typography: {
      fontSize: 14,
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"'
      ].join(',')
    }
  },
  // Theme configurations
  themes: {
    // Light theme configuration
    light: {
      colors: {
        primary: {
          main: '#ff3700'
        },
        secondary: {
          main: '#221E20'
        },
        background: {
          default: '#ffffff',
          paper: '#ffffff'
        },
        text: {
          primary: '#000000'
        }
      },
      variables: {
        bgColor: 'white',
        subBgColor: 'white',
        alterBgColor: '#f5f5f5',
        alterGradBgColor: 'linear-gradient(45deg, rgba(246,247,249,1) 0%, rgba(250,251,253,1) 100%)',
        alertBg: '#FFE8E8',
        alertBgGreen: '#EDF7ED',
        alertBgYellow: '#FFF4E5',
        alertBgRed: '#FBECED',
        textColor: 'black',
        lowTextColor: 'rgb(156 163 175)',
        mainColor: '#ff3700',
        logoColor: 'rgb(0, 0, 0)',
        altColor: '#696a6d',
        rsStepsStateProcess: 'rgb(255, 55, 0)',
        rsStepsStateFinish: 'rgb(255, 55, 0)',
        loginBg: 'rgb(136, 30, 0)',
        iconInvert: '',
        iconInvertActive:
          'invert(100%) sepia(0%) saturate(2%) hue-rotate(4deg) brightness(111%) contrast(101%) !important',
        mainColorHover: 'rgb(211, 46, 0)',
        navbarWidth: '100px'
      }
    },

    // Dark theme configuration
    dark: {
      colors: {
        primary: {
          main: '#ff3700'
        },
        secondary: {
          main: '#ffffff'
        },
        background: {
          default: '#181818',
          paper: '#282828'
        },
        text: {
          primary: '#ffffff'
        },
        action: {
          active: '#696a6d'
        }
      },
      variables: {
        bgColor: '#1c2524',
        subBgColor: '#282828',
        alterBgColor: '#333333',
        alterGradBgColor: 'linear-gradient(45deg, rgba(28,37,36,1) 0%, rgba(40,40,40,1) 100%)',
        alertBg: '#B60000',
        alertBgGreen: '#2E7D32',
        alertBgYellow: '#F57C00',
        alertBgRed: '#D32F2F',
        textColor: 'white',
        lowTextColor: 'rgb(156 163 175)',
        mainColor: '#ff3700',
        logoColor: 'rgb(255, 255, 255)',
        altColor: '#696a6d',
        rsStepsStateProcess: 'rgb(255, 55, 0)',
        rsStepsStateFinish: 'rgb(255, 55, 0)',
        loginBg: 'rgb(136, 30, 0)',
        iconInvert: 'invert(1)',
        iconInvertActive: '',
        mainColorHover: 'rgb(211, 46, 0)',
        navbarWidth: '100px'
      }
    },

    // High contrast theme configuration (WCAG 2.1 compliant)
    highContrast: {
      colors: {
        primary: {
          main: '#ff3700'
        },
        secondary: {
          main: '#ffffff'
        },
        background: {
          default: '#000000',
          paper: '#000000'
        },
        text: {
          primary: '#ffffff'
        },
        action: {
          active: '#ffffff'
        }
      },
      variables: {
        bgColor: '#1c2524',
        subBgColor: '#000000',
        alertBg: '#B60000',
        alterGradBgColor: '#B60000',
        alertBgGreen: '#EDF7ED',
        alertBgYellow: '#FFF4E5',
        textColor: 'white',
        lowTextColor: 'rgb(156 163 175)',
        mainColor: '#ff3700',
        logoColor: 'rgb(255, 255, 255)',
        altColor: '#696a6d',
        rsStepsStateProcess: 'rgb(255, 55, 0)',
        rsStepsStateFinish: 'rgb(255, 55, 0)',
        loginBg: 'rgb(136, 30, 0)',
        iconInvert: 'invert(1)',
        iconInvertActive: '',
        mainColorHover: 'rgb(211, 46, 0)',
        navbarWidth: '100px'
      }
    },

    // Christmas theme configuration
    christmas: {
      colors: {
        primary: {
          main: '#ff3700'
        },
        secondary: {
          main: '#ffffff'
        },
        background: {
          default: '#0f4c3a',
          paper: '#0f4c3a'
        },
        text: {
          primary: '#ffffff'
        },
        action: {
          active: '#ffffff'
        }
      },
      variables: {
        bgColor: '#0f4c3a',
        subBgColor: '#0f4c3a',
        alterBgColor: '#1a5c4a',
        alterGradBgColor: 'linear-gradient(45deg, rgba(15,76,58,1) 0%, rgba(26,92,74,1) 100%)',
        alertBg: '#B60000',
        alertBgGreen: '#2E7D32',
        alertBgYellow: '#F57C00',
        textColor: 'white',
        lowTextColor: 'rgb(156 163 175)',
        mainColor: '#ff3700',
        logoColor: 'rgb(255, 255, 255)',
        altColor: '#696a6d',
        rsStepsStateProcess: 'rgb(255, 55, 0)',
        rsStepsStateFinish: 'rgb(255, 55, 0)',
        loginBg: 'rgb(136, 30, 0)',
        iconInvert: '',
        iconInvertActive:
          'invert(100%) sepia(0%) saturate(2%) hue-rotate(4deg) brightness(111%) contrast(101%) !important',
        mainColorHover: 'rgb(211, 46, 0)',
        navbarWidth: '100px'
      }
    }
  }
};

export default clientConfig;
